import React, { useState, useEffect, useMemo, useCallback } from "react";
import { isEqual } from "lodash";
import { useTranslation } from "react-i18next";

import {
  Grid,
  Popover,
  TextField,
  Typography,
  FormControl,
  Divider,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleUp, faAngleDown } from "@fortawesome/free-solid-svg-icons";

import { Props, QuickFilterOption } from "./interfaces";
import { StyledTextfield, Menu, MenuItem } from "./styles";
import { is } from "date-fns/locale";

function QuickFilter<O, Q>({
  anchorOrigin,
  transformOrigin,
  options,
  quickFilters,
  quickFilter,
  handleChangeQuickFilter,
  defaultLabel,
  children,
  customWidth,
  isMultiSelect,
  multiSelectField,
  customTextFieldStyle,
  allItems,
  shrinkedLabel,
  loadNewItems,
  loading = false,
  forceClose,
  getMultipleSelectionLabel,
}: Props<O, Q>): JSX.Element {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | (EventTarget & Element)>(null);

  const [multiOptions, setMultiOptions] = useState<O[keyof O][]>([]);

  const isArrayAndLength = useCallback((obj: any): { isArray: boolean; arrayLength: number } => {
    return Symbol.iterator in Object(obj)
      ? Object(obj).length === 0
        ? { isArray: true, arrayLength: 0 }
        : { isArray: true, arrayLength: Object(obj).length }
      : { isArray: false, arrayLength: 0 };
  }, []);

  //#region set multi options
  useEffect(() => {
    const multiSelectFieldValue = quickFilters[quickFilter][multiSelectField as keyof Q[keyof Q]];
    const { isArray, arrayLength } = isArrayAndLength(multiSelectFieldValue);

    if (
      //if set to multiple select and receive multiple options ids from the useQuickfilter
      !!multiSelectField &&
      isMultiSelect &&
      isArray &&
      arrayLength > 0
    ) {
      const multi_options = multiSelectFieldValue as O[keyof O][];

      //when single value is needed but multiple are provided => sort the list according to their ids and pick the first one
      //this is implemented in line with currency quickfilter that relies on the chosen legalentity
      // setMultiOptions([
      //                   ...multi_options.map((_option)=>[_option] as O[keyof O])
      //                                   .sort( (a,b ) =>{
      //                                                   if (a < b)
      //                                                     return -1;
      //                                                   if ( a > b)
      //                                                     return 1;
      //                                                   return 0;
      //                                                 })
      //               ])
      setMultiOptions([...multi_options.map((_option) => [_option] as O[keyof O])]);
    }
    // when multiple values provided in quickfilters but Quickfilter is set to single selection
    //=> pick the first option in options list
    if (!!options && !isMultiSelect && isArray && arrayLength > 1) {
      !!options[0]?.value && handleChangeQuickFilter(quickFilter, options[0].value, allItems);
    }
  }, [
    quickFilters,
    quickFilter,
    multiSelectField,
    setMultiOptions,
    isMultiSelect,
    handleChangeQuickFilter,
    options,
    isArrayAndLength,
  ]);
  //#endregion

  useEffect(() => {
    if (forceClose?.current) {
      forceClose.current = false;

      setAnchorEl(null);
    }
  }, [forceClose?.current]);

  const handleClick = (event: React.MouseEvent) => {
    loadNewItems && loadNewItems();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);

  //#region getlabel
  const getLabel = (options: QuickFilterOption<O>[]) => {
    let foundOption: QuickFilterOption<O> | undefined;

    isMultiSelect && multiSelectField && multiOptions.length === 1
      ? (foundOption = options.find((option) => isEqual(option.value[multiSelectField], multiOptions[0])))
      : !isMultiSelect && multiSelectField && multiOptions.length > 0
      ? (foundOption = options.find((option) => isEqual(option.value[multiSelectField], multiOptions[0])))
      : (foundOption = options.find((option) => isEqual(option.value, quickFilters[quickFilter])));

    if (customWidth && foundOption) {
      return foundOption
        ? !!foundOption.code
          ? foundOption.code
          : foundOption.name.length < 35
          ? foundOption.name
          : `${foundOption.name.slice(0, 35)}...`
        : defaultLabel;
    }
    return foundOption
      ? !!foundOption.code
        ? foundOption.code
        : foundOption.name.length < 22
        ? foundOption.name
        : `${foundOption.name.slice(0, 22)}...`
      : isMultiSelect && multiSelectField && multiOptions.length > 1
      ? !!getMultipleSelectionLabel
        ? getMultipleSelectionLabel(multiOptions) ?? t(`components.quickfilters.multiple_selection`)
        : t(`components.quickfilters.multiple_selection`)
      : defaultLabel;
  };
  //#endregion

  //#region is selected
  const handleIsSelected = useCallback(
    (option: QuickFilterOption<O>, idx: number): boolean => {
      if (isMultiSelect && multiSelectField && multiOptions.length === 0) {
        const selectedOption = options.find((option) => isEqual(option.value, quickFilters[quickFilter]));

        if (!!selectedOption) {
          const multiSelectOptionValue = selectedOption?.value[multiSelectField];
          const { isArray } = isArrayAndLength(multiSelectOptionValue);

          isArray && setMultiOptions([selectedOption.value[multiSelectField]]);
        }
      }

      //Useful when all option is predefined list instead of empty array
      if (isMultiSelect && multiSelectField && multiOptions.length === options.length - 1) {
        return (option.value[multiSelectField] as Array<string>).length === options.length - 1;
      }

      return isMultiSelect && multiSelectField && multiOptions.length > 0
        ? multiOptions.filter((opt) => isEqual(opt, option?.value[multiSelectField])).length > 0
        : !isMultiSelect && multiSelectField && multiOptions.length > 0
        ? isEqual(multiOptions[0], option?.value[multiSelectField])
        : options?.findIndex(({ value }) => option && isEqual(value, quickFilters[quickFilter])) === idx;
    },
    [isMultiSelect, multiOptions, multiSelectField, options, quickFilter, quickFilters, isArrayAndLength]
  );
  //#endregion

  return (
    <Grid>
      <FormControl>
        <StyledTextfield
          // label={shrinkedLabel??""}
          // InputLabelProps={{
          //   shrink: true,
          // }}
          InputProps={{
            disabled: false,
            ...(shrinkedLabel && {
              startAdornment: (
                <InputAdornment
                  position={"start"}
                  style={{ display: "flex", height: "100%", gap: "2px" }}
                >
                  <Typography>{shrinkedLabel}</Typography>
                  <Divider
                    orientation={"vertical"}
                    flexItem
                  />
                </InputAdornment>
              ),
            }),
            endAdornment: (
              <>
                {loading && (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <CircularProgress
                      color="inherit"
                      size={20}
                      sx={{
                        position: "absolute",
                        right: "6px",
                        top: "15px",
                        bottom: "0",
                      }}
                    />
                  </div>
                )}
                <FontAwesomeIcon icon={open ? faAngleUp : faAngleDown} />
              </>
            ),
            inputProps: {
              sx: {
                cursor: "pointer",
                textOverflow: "ellipsis",
                caretColor: "transparent",
              },
            },
            sx: {
              // ...customTextFieldStyle,
              width: customWidth ?? "inherit",
              "&::before": {
                borderBottom: `none`,
              },

              "&:hover:not(.Mui-disabled):before": {
                borderBottom: `none`,
              },

              "&:after": {
                borderBottom: `none`,
              },
            },
          }}
          variant="standard"
          onClick={handleClick}
          value={getLabel(options) ? getLabel(options) : defaultLabel ? defaultLabel : ""}
        />
        <Menu
          open={open}
          onClose={handleClose}
          anchorEl={anchorEl}
          anchorOrigin={anchorOrigin ?? { vertical: "bottom", horizontal: "left" }}
          transformOrigin={transformOrigin ?? { vertical: "top", horizontal: "left" }}
        >
          {loading ? (
            <>
              <MenuItem disabled={true}>{`Loading...`}</MenuItem>
            </>
          ) : options.length > 0 ? (
            options.map((option: any, idx: number) => (
              <MenuItem
                key={idx}
                title={option.name}
                selected={handleIsSelected(option, idx)}
                onClick={async () => {
                  const multiSelectFieldValue = option.value[multiSelectField as keyof O];
                  const { isArray, arrayLength } = isArrayAndLength(multiSelectFieldValue);

                  if (isMultiSelect && !!multiSelectField) {
                    if (isArray && arrayLength === 0) {
                      setMultiOptions([]);
                      handleChangeQuickFilter(quickFilter, option.value, allItems);
                      setAnchorEl(null);
                    } else if (isArray && arrayLength > 0) {
                      const noEmptyObjects = multiOptions.filter((_option) => Object(_option).length > 0);

                      if (
                        (!!options.find(
                          (_option) => (_option.value[multiSelectField] as Array<string>).length === options.length - 1
                        ) &&
                          multiOptions.length === options.length - 1) ||
                        (option.value[multiSelectField] as Array<string>).length === options.length - 1
                      ) {
                        handleChangeQuickFilter(quickFilter, option.value, allItems);
                      } else if (
                        multiOptions.filter((_option) => isEqual(_option, option.value[multiSelectField])).length === 0
                      ) {
                        //add new selection
                        setMultiOptions([...noEmptyObjects, option.value[multiSelectField]]);
                        const multiValue = {
                          ...option.value,
                          [multiSelectField]: [...noEmptyObjects, option.value[multiSelectField]],
                        };

                        let concatValues: any[] = [];

                        multiValue[multiSelectField].map((singleValue: O[keyof O], idx: number) => {
                          const { isArray, arrayLength } = isArrayAndLength(singleValue);

                          isArray && arrayLength > 0
                            ? concatValues.push(...Object(singleValue))
                            : concatValues.push(singleValue);

                          if (idx === multiValue[multiSelectField].length - 1)
                            handleChangeQuickFilter(
                              quickFilter,
                              {
                                ...multiValue,
                                [multiSelectField]: concatValues,
                              },
                              allItems
                            );

                          return null;
                        });
                      } else {
                        // remove selection

                        const filteredOptions = multiOptions.filter(
                          (_option) => !isEqual(_option, option.value[multiSelectField])
                        );

                        setMultiOptions(filteredOptions);
                        const multiValue = {
                          ...option.value,
                          [multiSelectField]: filteredOptions,
                        };

                        let concatValues: any[] = [];

                        multiValue[multiSelectField].map((singleValue: O[keyof O], idx: number) => {
                          const { isArray, arrayLength } = isArrayAndLength(singleValue);

                          isArray && arrayLength > 0
                            ? concatValues.push(...Object(singleValue))
                            : concatValues.push(singleValue);

                          if (idx === multiValue[multiSelectField].length - 1)
                            handleChangeQuickFilter(
                              quickFilter,
                              {
                                ...multiValue,
                                [multiSelectField]: concatValues,
                              },
                              allItems
                            );
                          return null;
                        });
                      }
                    }
                  } else {
                    handleChangeQuickFilter(quickFilter, option.value, allItems);
                    setAnchorEl(null);
                  }
                }}
              >
                {option.code ? `${option.code} (${option.name})` : option.name}
              </MenuItem>
            ))
          ) : (
            <Typography>{`${t(`components.quickfilters.no_options_message`)}`}</Typography>
          )}
          {children}
        </Menu>
      </FormControl>
    </Grid>
  );
}

export default QuickFilter;
